// The opening scene.
//
// The splash is a second and a half of courtesy — the game is already in memory
// and already running behind it — so instead of two gorillas standing on their
// perches waiting for somebody to press a button, they come in from opposite
// corners, have a look round, and lob a banana at each other while the logo is up.
//
// It is an anecdote and nothing else: no sound, no explosion, no crater, no screen
// shake. The bananas are not the match's projectile but a list of their own that
// nothing in flow.js can see, so nothing in here can touch a match or be touched
// by one. It stops the moment a real one starts.
//
// It moves the real gorillas rather than drawing new ones: the same sprites, the
// same fist position, the same gravity. That is why it costs almost nothing and
// still looks like the game rather than like a cutscene.

import { H, W, withCanvas } from './canvas.js';
import { state } from './state.js';
import {
  ACCESSORIES,
  GREET_CORNER,
  GREET_GAP_MAX_MS,
  GREET_GAP_MIN_MS,
  GREET_HIDE,
  GREET_HIDE_SPREAD,
  GREET_HIGH,
  GREET_INSET,
  GREET_OFF,
  GREET_SCALE,
  GREET_SLIDE_MS,
  GREET_TOP_Y,
  GREET_WAVE_MS,
  GREET_FEET,
  SPECIES,
  SPLASH_AIM_JITTER,
  SPLASH_AIM_SPREAD,
  SPLASH_DROP_JITTER,
  SPLASH_ENTER_MS,
  SPLASH_FLIGHT_JITTER,
  SPLASH_GAP_JITTER_MS,
  SPLASH_LANDING_Y,
  SPLASH_LOOK_MS,
  SPLASH_REPEAT_CHANCE,
  SPLASH_ENTRY_SPREAD,
  SPLASH_RISE,
  SPLASH_SPIN_MAX,
  SPLASH_SPIN_MIN,
  SPLASH_THROW_EVERY_MS,
  SPLASH_THROW_FRAMES,
} from './config.js';
import { greetCanvas } from './dom.js';
import { mods } from './modes.js';
import { drawGorilla, gorillaFistPos } from './render/gorilla.js';
import { clamp, rand } from './utils.js';

let cast = [];
let startedAt = 0;
let nextThrow = 0;
let throwing = 0;
let shots = [];

/**
 * The visitor up on the stage, or null while there is nobody out.
 *
 * One at a time, and never the same twice: a different species, different clothes and a
 * different spot on the frame. They are nobody's character — the two sides' looks
 * belong to the match, and a puppet wearing somebody's face would be changing their
 * gorilla from the title screen.
 *
 * They go on a canvas of their own, over the front end's cards, because the game canvas
 * is behind them: see withCanvas in canvas.js.
 */
let visitor = null;
let greetPhase = 'wait';
let greetAt = 0;
let visits = 0;

/** The surface the puppets are painted on, and the ink for it. */
const greetInk = greetCanvas ? greetCanvas.getContext('2d') : null;

/** The bananas in the air, for the renderer that owns the banana sprite. */
export function splashShots() {
  return shots;
}

/**
 * The house's own puppet: a duck in a pink bow, and the one the walk starts and ends on.
 *
 * Read by name rather than by index. A species list that grows must not quietly turn her
 * into somebody else, and if she were ever taken out of it this is a duck-shaped hole that
 * falls back to the first species rather than a crash.
 */
const PATITA = SPECIES.findIndex(s => s.id === 'duck');

/**
 * What this visitor is wearing.
 *
 * A counter rather than dice: one visitor after another walks through the species and
 * the wardrobe, where dice can show the same gorilla in the same hat three times
 * running — and the point of them is to show what there is to wear. Where they come out
 * and how long they stay are random; who they are is not.
 */
function greetingLook() {
  // The first one on is always her, and one whole walk of the wardrobe later it is her
  // again: visit 1, then visit 1 + however many species there are. Everything in between
  // is the walk. She is the hello, and the hello is worth repeating.
  if ((visits - 1) % SPECIES.length === 0) {
    return { species: Math.max(0, PATITA), worn: { hat: 'bow' } };
  }

  const pools = ['hat', 'face', 'neck'].map(slot => ACCESSORIES.filter(a => a.slot === slot));
  const worn = {};

  // Two of the three slots at a time, and which two walks round with the visitors:
  // dressed, without turning each one into a shop window.
  pools.forEach((pool, k) => {
    if (!pool.length || (visits + k) % 3 === 2) return;
    worn[pool[0].slot] = pool[(visits * 2 + k) % pool.length].id;
  });

  return { species: (visits * 2) % SPECIES.length, worn };
}

/**
 * Sets the scene from wherever the gorillas already are, so it never has to know
 * how a city places anybody: their perch is the destination.
 */
export function startSplashScene() {
  if (!state.players || state.players.length < 2) return;
  if (!state.players[0] || !state.players[1]) return;

  cast = state.players.map((p, i) => ({
    homeX: p.x,
    homeY: p.y,
    fromX: i ? W + 30 : -30,
    // A little below the roofline, so they rise into place as they come in and
    // arrive from the corner rather than fading in on the spot — and a random way further
    // down than that, because the fixed 26 put the two of them in exactly the same spot on
    // the same margin every single time the scene started. Where they cross the edge of the
    // picture is what this decides; it is clamped so the entrance is always on screen.
    fromY: Math.min(p.y + SPLASH_RISE + rand(0, SPLASH_ENTRY_SPREAD), H - 20),
  }));

  state.players.forEach((p, i) => {
    p.x = cast[i].fromX;
    p.y = cast[i].fromY;
  });

  startedAt = 0;
  throwing = 0;
  shots = [];

  // The greeting waits for the fight to be under way: the walk-on is the scene's own
  // entrance, and two shows at once is a muddle.
  visitor = null;
  greetPhase = 'wait';
  greetAt = 0;
}

/** Hands the gorillas back. The city putting them on their perches is makeCity's job. */
export function endSplashScene() {
  cast = [];
  shots = [];
  visitor = null;
  greetPhase = 'wait';
  greetAt = 0;
  // And the stage goes with it: the puppets are on a canvas of their own, and nothing
  // else clears that one.
  clearGreeting();
}

/**
 * One visitor, from a random spot on the frame, and then gone.
 *
 * One at a time on purpose: two of them leaning in together is a chorus, and the frame
 * is a stage with room for one. Where it comes from is a random edge and a random place
 * along it, so it is never a fixture of the layout.
 */
function newVisitor() {
  visits += 1;

  // Out of the frame's own edges, and never out of the middle of one: a puppet that
  // turned up in the centre of the picture is not leaning in, it is just there.
  const id = ['left', 'right', 'bottom', 'top'][rand(0, 3)];
  const tall = rand(GREET_HIGH, H - 90);
  // Along a horizontal edge, only by a corner: see GREET_CORNER.
  const along = Math.random() < 0.5 ? rand(30, GREET_CORNER) : rand(W - GREET_CORNER, W - 30);
  // How much of the margin it stands behind this time, so that the frame cuts its feet and the
  // ankles and no two visits lean in by quite the same amount. See GREET_HIDE.
  const hide = rand(GREET_HIDE, GREET_HIDE + GREET_HIDE_SPREAD);

  const spot = {
    left: { x: GREET_INSET - hide, y: tall },
    right: { x: W - GREET_INSET + hide, y: tall },
    // The one on the bottom edge keeps its feet on the bottom edge of the frame.
    bottom: { x: along, y: H - GREET_FEET },
    top: { x: along, y: GREET_TOP_Y - hide },
  }[id];

  // Where it waits: off the frame, on the side it is coming in from.
  const wings = {
    left: { x: -GREET_OFF, y: tall },
    right: { x: W + GREET_OFF, y: tall },
    bottom: { x: along, y: H + GREET_OFF },
    top: { x: along, y: -GREET_OFF },
  }[id];

  return {
    // Which way the sprite looks. Every one of them looks inwards.
    idx: id === 'right' || id === 'top' ? 1 : 0,
    // And the slant it leans at, which is what makes it read as coming round the frame
    // rather than as standing in front of it: hanging in over the top means upside down
    // with the feet still outside, and round the right margin means on the slant. Only
    // the one on the bottom edge is upright.
    angle: { left: 45, right: -45, bottom: 0, top: -180 }[id],
    spot,
    wings,
    figure: {
      x: 0,
      y: 0,
      alive: true,
      asleep: false,
      look: greetingLook(),
      // Nothing retires an antic on a figure that is not one of the match's —
      // advanceAntics walks state.players and nothing else — so the wave is cleared
      // here by hand when it is over.
      antic: null,
      anticStarted: 0,
    },
  };
}

/**
 * The visitor's own clock: wait, lean in, wave, lean back out, wait.
 *
 * Not a pair, and not on a loop of fixed length: the wait between one and the next is
 * drawn fresh every time, which is what keeps a puppet show from becoming a metronome.
 */
function advanceGreeting(now) {
  clearGreeting();
  if (!greetInk) return;

  if (greetPhase === 'wait') {
    if (!greetAt) greetAt = now + SPLASH_ENTER_MS + SPLASH_LOOK_MS;
    if (now < greetAt) return;

    visitor = newVisitor();
    greetPhase = 'in';
    greetAt = now;
  }

  const t = now - greetAt;
  const figure = visitor.figure;

  if (greetPhase === 'in' || greetPhase === 'out') {
    const k = ease(clamp(t / GREET_SLIDE_MS, 0, 1));
    const from = greetPhase === 'in' ? visitor.wings : visitor.spot;
    const to = greetPhase === 'in' ? visitor.spot : visitor.wings;

    figure.x = from.x + (to.x - from.x) * k;
    figure.y = from.y + (to.y - from.y) * k;

    if (t >= GREET_SLIDE_MS) {
      if (greetPhase === 'in') {
        greetPhase = 'wave';
        greetAt = now;
        figure.antic = 'wave';
        figure.anticStarted = now;
      } else {
        visitor = null;
        greetPhase = 'wait';
        greetAt = now + rand(GREET_GAP_MIN_MS, GREET_GAP_MAX_MS);
        return;
      }
    }
  } else if (t >= GREET_WAVE_MS) {
    greetPhase = 'out';
    greetAt = now;
    figure.antic = null;
  }

  withCanvas(greetInk, () => {
    // Turned about its own feet, so it swings round the frame's corner rather than being
    // carried sideways: the anchor is where the sprite stands.
    greetInk.translate(figure.x, figure.y);
    greetInk.rotate((visitor.angle * Math.PI) / 180);
    // And enlarged about that same anchor, so it grows out of the spot it stands on rather
    // than out of the corner of the frame. `clearGreeting` puts the transform back every
    // frame, so this one does not stack.
    greetInk.scale(GREET_SCALE, GREET_SCALE);
    greetInk.translate(-figure.x, -figure.y);

    drawGorilla(visitor.figure, visitor.idx);
  });
}

/** An empty stage. Their canvas is their own, so nothing else will clear it. */
function clearGreeting() {
  if (!greetInk) return;

  // Straightened out first: a rotation left behind by the last visitor, or by an
  // unbalanced save inside the sprite code, would move the rectangle this clears.
  greetInk.setTransform(1, 0, 0, 1, 0, 0);
  greetInk.clearRect(0, 0, W, H);
}

/**
 * Re-reads where the gorillas belong.
 *
 * The city can be rebuilt while the scene is still up — the end of a round
 * schedules one, and the front end keeps running underneath it — and a scene
 * holding its own copy of the perch would go on pinning them to the rooftops of the
 * city that no longer exists. Measured on a rebuilt skyline: 114px and 202px above
 * their new roofs, which is exactly the levitating this looks like.
 *
 * The destination is updated and nothing else, so a walk-in already under way is
 * not restarted.
 */
export function resyncSplashScene() {
  if (!cast.length) return;

  cast = state.players.map((player, i) => ({ ...cast[i], homeX: player.x, homeY: player.y }));
}

function ease(u) {
  return 1 - Math.pow(1 - u, 3);
}

/**
 * One frame of the scene. Driven by the render loop, which runs on the front end
 * exactly as it does during a match — that is what makes the menus an attract
 * mode, and this is the first thing to take advantage of it.
 */
export function advanceSplashScene() {
  if (!cast.length) return;

  // A match owns the gorillas from the moment one is on screen, and there is more
  // than one way into a match: starting one goes through newMatch, but loading a
  // shared code builds the city and jumps straight to the game screen without
  // passing through it. Rather than trusting every route to remember to say so, the
  // scene gives up the moment the match screen appears — which is why it went on
  // lobbing bananas in a match that had been loaded from a code.
  if (state.matchLive || state.screen === 'game' || state.screen === 'pause') {
    endSplashScene();
    return;
  }

  const now = state.clock;

  // Armed on the first frame rather than when it was asked for: the boot happens
  // before the loop has ever set the clock, and a start time of zero would spend
  // the whole entrance on the first frame.
  if (!startedAt) {
    startedAt = now;
    nextThrow = now + SPLASH_ENTER_MS + SPLASH_LOOK_MS;
  }

  const t = now - startedAt;
  const walking = t < SPLASH_ENTER_MS;

  cast.forEach((c, i) => {
    const p = state.players[i];
    if (!p) return;

    if (walking) {
      const k = ease(clamp(t / SPLASH_ENTER_MS, 0, 1));
      p.x = c.fromX + (c.homeX - c.fromX) * k;
      p.y = c.fromY + (c.homeY - c.fromY) * k;
      return;
    }

    p.y = c.homeY;

    // Then a look round. A slow sway is all that needs: they already face each
    // other, and a proper head-turn would be a whole animation system for the
    // second and a half this is on screen for.
    const looking = t - SPLASH_ENTER_MS;
    p.x = looking < SPLASH_LOOK_MS
      ? c.homeX + Math.sin(looking / 230 + i * Math.PI) * 5 * (i ? 1 : -1)
      : c.homeX;
  });

  advanceShots();
  advanceGreeting(now);

  if (t >= SPLASH_ENTER_MS + SPLASH_LOOK_MS && now >= nextThrow) {
    lob(throwing);

    // Usually they take turns. Not always: a strict alternation is the other half
    // of the scene reading as a loop instead of as two animals messing about.
    if (Math.random() > SPLASH_REPEAT_CHANCE) throwing = 1 - throwing;

    nextThrow = now + SPLASH_THROW_EVERY_MS + rand(-SPLASH_GAP_JITTER_MS, SPLASH_GAP_JITTER_MS);
  }
}

/**
 * One lob, on the same solve the game uses for a thrown banana — the step before
 * the move, so the fall over n frames is g*n*(n+1)/2 — aimed at a spot over the
 * roofs rather than at the other gorilla. This is pantomime: a splash screen where
 * somebody dies is not an anecdote.
 */
function lob(idx) {
  const p = state.players[idx];
  if (!p) return;

  const start = gorillaFistPos(p, idx);

  // Off-centre each way so the two arcs never meet, and short of the rival — but
  // every number here is jittered, because a fixed launch point, a fixed target
  // and a fixed flight time is a loop, and a loop stops being an anecdote the
  // second time round.
  const spread = SPLASH_AIM_SPREAD * (1 + rand(-SPLASH_AIM_JITTER, SPLASH_AIM_JITTER));
  const target = {
    x: W / 2 + (idx ? -1 : 1) * spread,
    y: SPLASH_LANDING_Y + rand(-SPLASH_DROP_JITTER, SPLASH_DROP_JITTER),
  };

  const n = Math.max(24, Math.round(SPLASH_THROW_FRAMES + rand(-SPLASH_FLIGHT_JITTER, SPLASH_FLIGHT_JITTER)));
  const g = mods().gravity;

  shots.push({
    x: start.x,
    y: start.y,
    vx: (target.x - start.x) / n,
    vy: (target.y - start.y - g * n * (n + 1) / 2) / n,
    // Its own landing height, because they are no longer all the same one: the
    // filter below retires a banana when it gets there.
    landY: target.y,
    // And its own tumble, which is the half of the look the arcs cannot give.
    // Math.random directly rather than rand(): rand() is a whole-number helper, and
    // asked for 0.08 to 0.3 it returns 0 — which had every banana in the scene
    // sailing across without turning over at all.
    spin: (SPLASH_SPIN_MIN + Math.random() * (SPLASH_SPIN_MAX - SPLASH_SPIN_MIN))
      * (Math.random() < 0.5 ? -1 : 1),
    rot: 0,
  });
}

function advanceShots() {
  const g = mods().gravity;

  shots.forEach(s => {
    s.vy += g;
    s.x += s.vx;
    s.y += s.vy;
    s.rot = (s.rot || 0) + (s.spin || 0.16);
  });

  // Nothing dies and nothing explodes. A banana that has arrived is simply not
  // there any more, with a small puff off the roof so the gag reads as a miss
  // rather than as the picture losing track of it.
  shots = shots.filter(s => {
    if (s.y < s.landY && s.x > -40 && s.x < W + 40) return true;

    for (let i = 0; i < 5; i++) {
      state.debris.push({
        x: s.x,
        y: Math.min(s.y, s.landY),
        vx: rand(-1.5, 1.5),
        vy: rand(-2, 0.5),
        s: 1,
        c: '#cfd6e6',
      });
    }

    return false;
  });
}
